import { graphql } from 'react-relay';
import React from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	STAR_BUTTON_SHORTCUT,
	UNIVERSAL_STAR_BUTTON_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import type { ShortcutEvent } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	SpaceStarShortcutListenerIsSpaceFavouritedQuery as SpaceStarShortcutListenerIsSpaceFavouritedQuery$data,
	SpaceStarShortcutListenerIsSpaceFavouritedQueryVariables as SpaceStarShortcutListenerIsSpaceFavouritedQuery$variables,
} from './__types__/SpaceStarShortcutListenerIsSpaceFavouritedQuery.ts';
import { SpaceStar } from './SpaceStar.tsx';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const handleError = (error, createAnalyticsEvent) => {
	// Temporarily log errors to debug why some are not being marked as handled
	createAnalyticsEvent({
		type: 'sendOperationalEvent',
		data: {
			action: 'error',
			actionSubject: 'spaceStarShortcutListener',
			source: 'spaceStarShortcutListener',
			attributes: {
				error: JSON.stringify(error),
				isNotFoundError: isNotFoundError(error),
			},
		},
	}).fire();
	if (isNotFoundError(error)) {
		markErrorAsHandled(error);
	}
};

// Space starring only works for view mode for space overviews.
// Starring on an edit space overview uses PageStar, not SpaceStar.
export const SpaceStarShortcutListener: React.FC<{
	spaceKey: string;
	contentMode?: string;
	source?: string;
}> = ({ spaceKey, contentMode, source = 'FavouriteMoreActionsMenuItem' }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		data: spaceFavouriteStatusData,
		loading,
		error,
	} = useQueryInterop<
		SpaceStarShortcutListenerIsSpaceFavouritedQuery$data,
		SpaceStarShortcutListenerIsSpaceFavouritedQuery$variables
	>(
		graphql`
			query SpaceStarShortcutListenerIsSpaceFavouritedQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					id
					alias
					name
					key
					currentUser {
						isFavourited
					}
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			fetchPolicy: 'cache-first',
			onError: (error) => {
				handleError(error, createAnalyticsEvent);
			},
		},
	);

	if (loading) {
		return null;
	}

	if (error) {
		handleError(error, createAnalyticsEvent);
		return null;
	}

	const isStarred = spaceFavouriteStatusData?.space?.currentUser?.isFavourited || false;
	const spaceName =
		spaceFavouriteStatusData?.space?.name ||
		spaceFavouriteStatusData?.space?.alias ||
		spaceFavouriteStatusData?.space?.key ||
		'';
	const spaceId = spaceFavouriteStatusData?.space?.id || '';

	const handleShortcut = (toggleStar) => (e: KeyboardEvent | ShortcutEvent) => {
		toggleStar(e);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'spaceStar',
				attributes: {
					triggeredByKeyboardShortcut: true,
					action: isStarred ? 'unstar' : 'star',
					isInEditor: contentMode === 'edit',
				},
				source,
			},
		}).fire();
	};

	return (
		<SpaceStar
			spaceId={spaceId}
			spaceKey={spaceKey}
			isStarred={isStarred}
			spaceName={spaceName}
			shouldShowFlags
		>
			{({ toggle: toggleStar }) => (
				<GeneralShortcutListener
					deprecatedAccelerator={STAR_BUTTON_SHORTCUT}
					accelerator={UNIVERSAL_STAR_BUTTON_SHORTCUT}
					listener={handleShortcut(toggleStar)}
				/>
			)}
		</SpaceStar>
	);
};
